import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { match } from "ts-pattern";

import { Button } from "@/web/components/primitives/button";
import { Tooltip, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { ErrorHandler } from "@/web/lib/errors";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";

import { useAcknowledgments } from "./AcknowledgmentsProvider";
import { MESSAGE_KEYS } from "./messageKeys";
import { AckDialogShell, AckTooltipShell } from "./shells";
import { type ActionFilterCategory, type TourStep, type TourStepId, TOUR_STEPS } from "./tourSteps";

/**
 * Internal context for the orientation tour: lets tile components register
 * their DOM node as the anchor for a tour step via `useTourStep`. Scoped to
 * the tour because target-registration is a tour-specific mechanic — generic
 * ack modals don't need it.
 */
type TourTargetContextValue = {
  registerTarget: (id: TourStepId, el: HTMLElement | null) => void;
  /**
   * Advance past `id` if — and only if — it's the step currently showing.
   *
   * For steps that teach a gesture: once the user has performed the thing the
   * tooltip is describing, the tooltip is stale, and making them dismiss it
   * anyway reads as the tour not noticing. A no-op when a different step (or
   * no step) is active, so callers can fire it unconditionally.
   */
  completeStep: (id: TourStepId) => void;
};

const TourTargetContext = createContext<TourTargetContextValue>({
  registerTarget: () => {},
  completeStep: () => {},
});

export function useTourTargetContext() {
  return useContext(TourTargetContext);
}

/** Same step ids anchored to the same nodes — i.e. nothing worth re-rendering for. */
function sameTargets(
  a: ReadonlyMap<TourStepId, HTMLElement>,
  b: ReadonlyMap<TourStepId, HTMLElement>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [id, el] of a) if (b.get(id) !== el) return false;
  return true;
}

export function OrientationTour({
  children,
  onPickCategory,
}: {
  children: React.ReactNode;
  // Called when the user picks a category from the final choice step. The
  // page wires this to its filter state so the deck reflects the choice the
  // moment the tour ends.
  onPickCategory: (value: ActionFilterCategory) => void;
}) {
  const { underwritableEntityId, isAcked } = useAcknowledgments();
  const [index, setIndex] = useState(0);

  // Targets live in state so `activeStep`'s memo has an honest dependency —
  // the previous ref+`tick` indirection lied to react-hooks/exhaustive-deps.
  // The ref below is a staging buffer, not a second source of truth: `targets`
  // stays the published value everything reads, so that dependency is still
  // honest.
  const [targets, setTargets] = useState<ReadonlyMap<TourStepId, HTMLElement>>(() => new Map());
  const pendingTargets = useRef<Map<TourStepId, HTMLElement>>(new Map());

  // Publish only when the CONTENTS changed. Rebuilding the map unconditionally
  // made this component a re-render amplifier: any anchor tile re-rendering for
  // any reason produced new tour state, which re-rendered the tour, which
  // re-published its context value. Paired with a moving context value that was
  // an unbounded loop ("Maximum update depth exceeded" in the admin SMB
  // viewer); on its own it is still needless work on every tile render.
  const publishTargets = useCallback(() => {
    setTargets((prev) =>
      sameTargets(prev, pendingTargets.current) ? prev : new Map(pendingTargets.current),
    );
  }, []);

  // Ref callbacks run during commit, and Radix recomposes a `Slot`'s refs on
  // every render — so a tile re-rendering detaches (`el === null`) and
  // immediately re-attaches the SAME node within one commit. Writing that
  // straight to state publishes two updates whose net effect is nothing.
  // Staging in a ref and reconciling after the commit's ref calls have all run
  // lets that pair collapse to a no-op.
  const registerTarget = useCallback(
    (id: TourStepId, el: HTMLElement | null) => {
      if (el) pendingTargets.current.set(id, el);
      else pendingTargets.current.delete(id);
      queueMicrotask(publishTargets);
    },
    [publishTargets],
  );

  const { mutateAsync: acknowledge, isPending: isFinishing } = useReducerMutation(
    reducers.acknowledgeMessage,
    {
      // `ErrorHandler.default` already toasts the message. On failure, the
      // mutation's `isPending` flips back to false → the Next/Done button
      // re-enables, letting the user retry instead of being stuck.
      onError: ErrorHandler.default,
    },
  );

  const finishTour = useCallback(() => {
    void acknowledge({ underwritableEntityId, messageKey: MESSAGE_KEYS.orientation });
  }, [underwritableEntityId, acknowledge]);

  const next = useCallback(() => {
    setIndex((i) => {
      if (i + 1 >= TOUR_STEPS.length) {
        finishTour();
        return i;
      }
      return i + 1;
    });
  }, [finishTour]);

  const currentStep: TourStep | null = TOUR_STEPS[index] ?? null;

  // Tooltip steps wait for their target to register. Modal and choice steps don't need one.
  const activeStep = useMemo(() => {
    if (!currentStep) return null;
    if (currentStep.kind === "modal" || currentStep.kind === "choice") return currentStep;
    return targets.has(currentStep.id) ? currentStep : null;
  }, [currentStep, targets]);

  // Read the live step from a ref so `completeStep` stays referentially stable
  // — it ends up in the deps of the deck's skip handler, and a new identity on
  // every step change would churn that callback through the whole card tree.
  const currentStepIdRef = useRef<TourStepId | null>(null);
  currentStepIdRef.current = currentStep?.id ?? null;

  const completeStep = useCallback(
    (id: TourStepId) => {
      if (currentStepIdRef.current !== id) return;
      next();
    },
    [next],
  );

  const isLast = index === TOUR_STEPS.length - 1;
  const targetContext = useMemo<TourTargetContextValue>(
    () => ({ registerTarget, completeStep }),
    [registerTarget, completeStep],
  );

  return (
    <TourTargetContext.Provider value={targetContext}>
      {children}
      {!isAcked(MESSAGE_KEYS.orientation) && (
        <TourOverlay
          step={activeStep}
          isLast={isLast}
          isFinishing={isFinishing}
          getTarget={(id) => targets.get(id) ?? null}
          onNext={next}
          onSkip={finishTour}
          onPickCategory={(value) => {
            onPickCategory(value);
            finishTour();
          }}
        />
      )}
    </TourTargetContext.Provider>
  );
}

function TourOverlay({
  step,
  isLast,
  isFinishing,
  getTarget,
  onNext,
  onSkip,
  onPickCategory,
}: {
  step: TourStep | null;
  isLast: boolean;
  isFinishing: boolean;
  getTarget: (id: TourStepId) => HTMLElement | null;
  onNext: () => void;
  onSkip: () => void;
  onPickCategory: (value: ActionFilterCategory) => void;
}) {
  if (!step) return null;

  return match(step)
    .with({ kind: "modal" }, (s) => (
      // Key on step.id so pageIndex resets if the tour ever advances through
      // more than one modal step.
      <TourModal
        key={s.id}
        step={s}
        isLast={isLast}
        isFinishing={isFinishing}
        onNext={onNext}
        onSkip={onSkip}
      />
    ))
    .with({ kind: "tooltip" }, (s) => {
      const target = getTarget(s.id);
      if (!target) return null;
      // Key on step.id so the Tooltip unmounts between steps; Radix then runs
      // its open animation for the new bubble instead of swapping content in place.
      return (
        <TourTooltip
          key={s.id}
          step={s}
          target={target}
          isLast={isLast}
          isFinishing={isFinishing}
          onNext={onNext}
        />
      );
    })
    .with({ kind: "choice" }, (s) => (
      <TourChoice key={s.id} step={s} isFinishing={isFinishing} onPickCategory={onPickCategory} />
    ))
    .exhaustive();
}

function TourModal({
  step,
  isLast,
  isFinishing,
  onNext,
  onSkip,
}: {
  step: Extract<TourStep, { kind: "modal" }>;
  isLast: boolean;
  isFinishing: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const [pageIndex, setPageIndex] = useState(0);
  // `pageIndex` is bounded by construction, but `noUncheckedIndexedAccess`
  // can't see that. Bail rather than thread non-null assertions through JSX.
  const page = step.pages[pageIndex];
  if (!page) return null;
  const isLastPage = pageIndex === step.pages.length - 1;
  // Done writes the ack only when this is the last page of the last step.
  const isFinalClick = isLast && isLastPage;

  const advance = () => {
    if (isLastPage) onNext();
    else setPageIndex((p) => p + 1);
  };

  return (
    <AckDialogShell
      title={page.title}
      body={page.body}
      footer={
        <div className="flex w-full flex-row-reverse justify-between">
          <Button onClick={advance} color="blue" disabled={isFinalClick && isFinishing}>
            {isFinalClick ? "Done" : "Next"}
          </Button>
          <Button
            variant="link"
            className="text-muted-foreground"
            onClick={onSkip}
            disabled={isFinishing}
          >
            Skip tour
          </Button>
        </div>
      }
    />
  );
}

/**
 * Final tour step. Each button both writes the orientation ack (via
 * `onPickCategory`, which the parent wires to `finishTour`) and sets the
 * deck's category filter to the chosen value — so picking is the dismissal.
 */
function TourChoice({
  step,
  isFinishing,
  onPickCategory,
}: {
  step: Extract<TourStep, { kind: "choice" }>;
  isFinishing: boolean;
  onPickCategory: (value: ActionFilterCategory) => void;
}) {
  return (
    <AckDialogShell
      title={step.title}
      body={step.body}
      footer={
        <div className="grid w-full grid-cols-2 gap-2">
          {step.choices.map((c) => (
            <Button
              key={c.value}
              color={c.color}
              onClick={() => onPickCategory(c.value)}
              disabled={isFinishing}
            >
              {c.label}
            </Button>
          ))}
        </div>
      }
    />
  );
}

/**
 * Radix Tooltip positions relative to its `TooltipTrigger` child. Since the
 * actual target lives in a different subtree (a tile registered via callback
 * ref), we render an invisible fixed-position div that mirrors the target's
 * bounding rect and use *that* as the trigger. The rect is re-read on scroll
 * and resize so the bubble stays glued to the target.
 *
 * Alternatives considered and rejected:
 *   - `querySelector` lookup on the anchor element: imperative, loses
 *     reactivity, hydration-fragile.
 *   - Lifting the trigger into the tile itself: forces every tour-anchored
 *     tile to know about the tour and own a Tooltip, fighting any ack-tooltip
 *     wrapping the same anchor.
 */
function TourTooltip({
  step,
  target,
  isLast,
  isFinishing,
  onNext,
}: {
  step: Extract<TourStep, { kind: "tooltip" }>;
  target: HTMLElement;
  isLast: boolean;
  isFinishing: boolean;
  onNext: () => void;
}) {
  const ghostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ghost = ghostRef.current;
    if (!ghost) return;
    const sync = () => {
      const rect = target.getBoundingClientRect();
      ghost.style.top = `${rect.top}px`;
      ghost.style.left = `${rect.left}px`;
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(target);
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [target]);

  return (
    <Tooltip open>
      <TooltipTrigger asChild>
        <div ref={ghostRef} aria-hidden className="pointer-events-none fixed" />
      </TooltipTrigger>
      <AckTooltipShell
        side={step.side}
        color={step.color}
        title={step.title}
        body={step.body}
        footer={
          <Button variant="link" size="sm" onClick={onNext} disabled={isLast && isFinishing}>
            {isLast ? "Done" : "Next"}
          </Button>
        }
      />
    </Tooltip>
  );
}
